import type {
	AccountInfo,
	EmployeesRecord,
	RolesDataResponse,
	SelectOptionContext,
	TerminatedEmployeesRecord
} from '@/types'

export type Role = {
	id: number
	role: string
	description: string
	permissions: { checked: boolean; label: string }[]
	status?: 'active' | 'disabled'
}
export type TeamSchema = {
	id: number
	fullName?: string
	first_name: string
	last_name: string
	email: string
	gender: string
	phone: string
	role: string
	status?: string
}
export type StateWithLGAStore = Omit<LocalLgaStateContext, 'lgas'> & {
	lga?: SelectOptionContext
}
export const useUser = defineStore('user', {
	state: () => ({
		user: null as AccountInfo | null,
		walletInfo: null as WalletResponse | null,
		isLoggedIn: false,
		isAdmin: false,
		kycIncomplete: false,
		isKycModalOpen: false,
		cacModalOpen: false,
		teams: [] as TeamSchema[],
		roles: [] as RolesDataResponse[],
		gender: [
			{ value: 'male', label: 'Male' },
			{ value: 'female', label: 'Female' }
		],
		statesWithLga: [] as StateWithLGAStore[],
		employees: {
			active: [] as EmployeesRecord[],
			terminated: [] as TerminatedEmployeesRecord[]
		},
		sectors: [] as SelectOptionContext,
		permissions: [] as string[]
	}),
	getters: {
		User(state) {
			return state.user || getLocalStorage<AccountInfo | null>('context-state', true)
		},
		isAccountNew(state) {
			if (state.user?.created_at) {
				const createdDate = new Date(state.user.created_at)
				const existingDateInstance = new Date()
				existingDateInstance.setMonth(createdDate.getMonth())
				existingDateInstance.setDate(createdDate.getDate())
				existingDateInstance.setFullYear(createdDate.getFullYear())
				const currentDate = Date.now()
				return existingDateInstance.valueOf() === currentDate.valueOf()
			}
			return false
		},
		takeATour() {
			const toured = localStorage.getItem('toured')
			const { isMobile } = useMobile()
			const kycCompleted = Boolean(this.User?.customer?.sector)
			if (this.isAccountNew && !toured && location.pathname === '/' && !isMobile.value && kycCompleted) {
				return true
			}
			return false
		},
		Roles(state) {
			const response = state.roles.map(item => ({
				label: item?.name,
				value: item?.id
			}))
			return response
		},
		Gender(state) {
			return state.gender
		},
		Teams(state) {
			return state.teams
		},
		BusinessSectors(state) {
			return state.sectors
		},
		States(store) {
			return store.statesWithLga
		},
		StatesWithLga(store) {
			return store.statesWithLga
		},
		Employees(state) {
			return state.employees
		},
		getWalletDetails(state) {
			return state.walletInfo
		},
		CACVerified(state) {
			return Boolean(state.user?.customer?.meta?.cac_verified)
		},
		KYCCompleted(state) {
			return Boolean(state.user?.customer?.meta?.sector && Boolean(state.user?.email_verified_at))
		},
		IsAdmin(state) {
			return state.isAdmin
		}
	},
	actions: {
		setAccountInfo(payload: AccountInfo | null) {
			this.user = payload
			if (this.user?.first_name) {
				this.isLoggedIn = true
			}
			this.isAdmin = Boolean(String(this.user?.role ?? '').toLowerCase() === 'admin')
		},
		setAccountPermission(payload: PermissionsDataResponse[]) {
			// Store the permissions given to a specific account
			this.permissions = payload.map(item => item.name.toLowerCase())
			setStorageItem({ key: 'permissions', value: this.permissions, encrypt: true })
		},
		setBusinessSectors(payload: SelectOptionContext) {
			this.sectors = payload
		},
		updateBusinessInfo(payload: UserAccountContext['customer']) {
			if (this.user?.customer) {
				this.user.customer = payload
			}
		},
		setWalletDetails(details?: WalletResponse) {
			if (details) {
				this.walletInfo = details
			}
		},
		setRoles(payload: RolesDataResponse[]) {
			this.roles = payload
		},
		setLocalLgaState(payload: StateWithLGAStore[]) {
			this.statesWithLga = payload
		},
		setKyc(payload: boolean) {
			this.kycIncomplete = payload && this.IsAdmin
		},
		confirmAccountVerification() {
			if (!this.User?.email_verified_at && Boolean(this.User?.email)) {
				navigateTo('/verify-account')
			}
		},
		disableTour(fn?: Function) {
			localStorage.setItem('toured', 'true')
			if (fn) {
				fn()
			}
		},
		enableTour(fn?: Function) {
			if (this.takeATour) {
				// localStorage.removeItem('toured')
				if (fn) {
					fn()
				}
			}
			// Clear the storage if account is older than a day
			if (this.isAccountNew && this.User) {
				localStorage.removeItem('toured')
			}
		},
		toggleCACModal() {
			this.cacModalOpen = !this.cacModalOpen
		},
		checkCACVerification() {
			if (!this.User?.customer?.meta?.cac_verified && this.KYCCompleted) {
				this.toggleCACModal()
			}
		}
	}
})
